/**
 * Os ícones de 14 px que identificam telefone, e-mail e endereço no cartão da
 * agenda.
 *
 * Papel: cada função devolve o `<svg>` de um campo do cartão que
 * `ListaContatos` desenha para cada contato — traço de 1,5px, sem
 * preenchimento, pontas e junções arredondadas —, com o `path`, `rect` e
 * `circle` reproduzidos caractere a caractere.
 *
 * Como usar: quem monta um campo do cartão passa o componente inteiro, nunca o
 * elemento já montado — é `ListaContatos` quem decide o tamanho (14px) e a
 * cor de cada um, ao montar o `<li>` de um contato dentro do `.map()`. Todo
 * traço usa `stroke="currentColor"`, e é isso que faz o ícone herdar
 * `--texto-fraco` do campo ao lado, sem precisar de uma classe de cor própria
 * em cada `<svg>`. O `aria-hidden` já vem marcado em cada um, porque o texto do
 * campo, ao lado do ícone, já é o que um leitor de tela anuncia.
 */
import { type SVGProps } from 'react';

/** Atributos comuns aos três `<svg>`, para não repeti-los em cada componente. */
const ATRIBUTOS_COMUNS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * Ícone do campo de telefone: um bocal de telefone.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeTelefone(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <path d="M4 5c0 8.284 6.716 15 15 15h1a1 1 0 0 0 1-1v-3.2a1 1 0 0 0-.8-.98l-3.6-.72a1 1 0 0 0-1.05.44l-1.1 1.65a12.5 12.5 0 0 1-5.64-5.64l1.65-1.1a1 1 0 0 0 .44-1.05l-.72-3.6A1 1 0 0 0 8.2 4H5a1 1 0 0 0-1 1z" />
    </svg>
  );
}

/**
 * Ícone do campo de e-mail: um envelope.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeEmail(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <rect x="3" y="5" width="18" height="14" rx="1.5" />
      <path d="M3.5 6l8.5 6 8.5-6" />
    </svg>
  );
}

/**
 * Ícone do campo de endereço: um marcador de mapa.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeEndereco(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <path d="M12 21s-7-6.1-7-11a7 7 0 0 1 14 0c0 4.9-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </svg>
  );
}
